import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { DemoBadge } from "../../components/bits";

// ---------------- Schemas ----------------
export const SCHEMAS = {
  services: {
    title: "Services", resource: "services",
    columns: [["name", "Name"], ["starting_price", "From £/hr"], ["min_hours", "Min hrs"], ["active", "Active"]],
    fields: [["name", "Name", "text"], ["description", "Description", "textarea"], ["starting_price", "Starting price (£/hr)", "number"],
      ["min_hours", "Minimum hours", "number"], ["duration", "Estimated duration", "text"],
      ["add_ons", "Add-ons (comma separated)", "tags"], ["areas", "Service areas (comma separated)", "tags"],
      ["instant_booking", "Instant booking allowed", "switch"], ["manual_quote", "Manual quotation required", "switch"],
      ["active", "Active", "switch"], ["order", "Order", "number"]],
  },
  packages: {
    title: "Pricing Packages", resource: "packages",
    columns: [["name", "Name"], ["type", "Type"], ["rate", "Rate £"], ["min_hours", "Min hrs"], ["visible", "Visible"]],
    fields: [["name", "Name", "text"], ["type", "Type", "select", ["hourly", "fixed", "from", "bespoke", "recurring"]],
      ["rate", "Rate (£)", "number"], ["unit", "Unit", "text"], ["min_hours", "Minimum hours", "number"],
      ["description", "Description", "textarea"], ["visible", "Visible on site", "switch"], ["order", "Order", "number"]],
  },
  service_areas: {
    title: "Service Areas", resource: "service_areas",
    columns: [["name", "Area"], ["status", "Status"], ["postcodes", "Postcodes"]],
    fields: [["name", "Area name", "text"], ["status", "Status", "select", ["Active", "Limited", "Coming Soon", "Unavailable"]],
      ["postcodes", "Postcodes", "text"], ["order", "Order", "number"]],
  },
  contacts: {
    title: "CRM Contacts", resource: "contacts",
    columns: [["name", "Name"], ["type", "Type"], ["stage", "Stage"], ["email", "Email"], ["phone", "Phone"]],
    fields: [["name", "Name", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"],
      ["type", "Type", "select", ["Lead", "Prospect", "Customer", "Commercial Client", "Landlord / Agent", "Supplier", "Partner"]],
      ["stage", "Stage", "select", ["New Lead", "Contacted", "Quote Requested", "Quote Sent", "Follow-up", "Won", "Lost"]],
      ["source", "Source", "text"], ["address", "Address", "text"], ["notes", "Notes", "textarea"], ["next_follow_up", "Next follow-up", "date"]],
  },
  staff: {
    title: "Staff & Cleaners", resource: "staff", sensitive: true,
    columns: [["name", "Name"], ["worker_status", "Status"], ["dbs_status", "DBS"], ["right_to_work", "RTW"], ["active", "Active"]],
    fields: [["name", "Name", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"], ["emergency_contact", "Emergency contact", "text"],
      ["worker_status", "Worker status", "select", ["Self-employed", "Employed", "Contractor", "Applicant"]],
      ["start_date", "Start date", "date"], ["availability", "Availability", "text"], ["service_areas", "Service areas", "tags"],
      ["skills", "Skills", "tags"], ["dbs_status", "DBS status", "select", ["Not Started", "Pending", "Cleared", "Expired"]],
      ["dbs_reference", "DBS reference", "text"], ["dbs_date", "DBS date", "date"],
      ["right_to_work", "Right to work", "select", ["Verified", "Pending", "Not Verified"]],
      ["insurance_status", "Insurance/compliance", "select", ["Not Started", "Pending", "Active", "Expired"]],
      ["performance_notes", "Performance notes", "textarea"], ["active", "Active", "switch"]],
  },
  applicants: {
    title: "Recruitment", resource: "applicants",
    columns: [["name", "Name"], ["postcode", "Postcode"], ["stage", "Stage"], ["email", "Email"]],
    fields: [["name", "Name", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"], ["postcode", "Postcode", "text"],
      ["stage", "Stage", "select", ["Applied", "Screening", "Interview", "Documents", "Approved", "Onboarding", "Active", "Rejected"]],
      ["experience", "Experience", "textarea"], ["availability", "Availability", "text"], ["areas", "Areas", "text"],
      ["driving", "Driving", "text"], ["right_to_work", "Right to work", "text"], ["notes", "Notes", "textarea"]],
  },
  tasks: {
    title: "Tasks", resource: "tasks",
    columns: [["title", "Task"], ["priority", "Priority"], ["assigned_to", "Assigned"], ["due_date", "Due"], ["status", "Status"]],
    fields: [["title", "Task", "text"], ["priority", "Priority", "select", ["Low", "Normal", "High", "Urgent"]],
      ["assigned_to", "Assigned to", "text"], ["due_date", "Due date", "date"],
      ["status", "Status", "select", ["Open", "In Progress", "Completed"]], ["notes", "Notes", "textarea"]],
  },
  inventory: {
    title: "Inventory & Supplies", resource: "inventory",
    columns: [["name", "Item"], ["type", "Type"], ["stock", "Stock"], ["reorder_level", "Reorder"], ["location", "Location"]],
    fields: [["name", "Item", "text"], ["type", "Type", "select", ["Product", "Equipment"]], ["stock", "Stock level", "number"],
      ["reorder_level", "Reorder level", "number"], ["supplier", "Supplier", "text"], ["cost", "Cost (£)", "number"],
      ["location", "Storage location", "text"], ["assigned_to", "Assigned worker/kit", "text"]],
  },
  supply_requests: {
    title: "Supply Requests", resource: "supply_requests", readOnlyCreate: true,
    columns: [["item", "Item"], ["quantity", "Qty"], ["requested_by", "By"], ["status", "Status"]],
    fields: [["item", "Item", "text"], ["quantity", "Quantity", "number"], ["status", "Status", "select", ["Requested", "Approved", "Fulfilled", "Declined"]], ["notes", "Notes", "textarea"]],
  },
  compliance_items: {
    title: "Compliance Centre", resource: "compliance_items", sensitive: true,
    columns: [["name", "Item"], ["status", "Status"], ["renewal_date", "Renewal"]],
    fields: [["name", "Item", "text"], ["status", "Status", "select", ["Not Started", "Pending", "Active", "Renewal Due", "Expired"]],
      ["reference", "Reference", "text"], ["renewal_date", "Renewal date", "date"], ["notes", "Notes", "textarea"]],
  },
  documents: {
    title: "Document Centre", resource: "documents", sensitive: true,
    columns: [["name", "Document"], ["category", "Category"], ["access", "Access"]],
    fields: [["name", "Document name", "text"], ["category", "Category", "select", ["Insurance", "Policies", "Risk Assessment", "Contract", "Customer Agreement", "Staff Document", "Supplier Agreement", "Training", "Marketing"]],
      ["access", "Access level", "select", ["Management", "Staff", "Supervisor"]], ["notes", "Notes", "textarea"], ["file_url", "File reference / URL", "text"]],
  },
  commercial_clients: {
    title: "Commercial Clients", resource: "commercial_clients",
    columns: [["name", "Client"], ["sites", "Sites"], ["contract_value", "Contract £"], ["status", "Status"]],
    fields: [["name", "Client / company", "text"], ["contact_name", "Site contact", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"],
      ["sites", "Sites (comma separated)", "tags"], ["schedule", "Cleaning schedule", "text"], ["contract_value", "Contract value (£)", "number"],
      ["sla_notes", "SLA notes", "textarea"], ["status", "Status", "select", ["Prospect", "Active", "On Hold", "Ended"]]],
  },
  campaigns: {
    title: "Marketing", resource: "campaigns",
    columns: [["name", "Campaign"], ["channel", "Channel"], ["budget", "Budget £"], ["leads", "Leads"], ["revenue", "Revenue £"]],
    fields: [["name", "Campaign", "text"], ["channel", "Channel", "select", ["Google", "Instagram", "Facebook", "LinkedIn", "Nextdoor", "Email", "Leaflets", "Referrals", "Other"]],
      ["budget", "Budget (£)", "number"], ["leads", "Leads", "number"], ["customers", "Customers acquired", "number"], ["revenue", "Revenue (£)", "number"], ["notes", "Notes", "textarea"]],
  },
  referrals: {
    title: "Referrals", resource: "referrals",
    columns: [["referrer", "Referrer"], ["referred", "Referred"], ["status", "Status"]],
    fields: [["referrer", "Referred by", "text"], ["referred", "New customer", "text"], ["status", "Status", "select", ["Pending", "Converted", "Lapsed"]], ["notes", "Notes (discounts require approval)", "textarea"]],
  },
  key_records: {
    title: "Key-Holding & Access", resource: "key_records", sensitive: true,
    columns: [["property", "Property"], ["key_held", "Held"], ["key_ref", "Ref"], ["responsible", "Responsible"]],
    fields: [["property", "Property / customer", "text"], ["key_held", "Key held", "switch"], ["key_ref", "Key reference", "text"],
      ["collection_date", "Collection date", "date"], ["return_date", "Return date", "date"],
      ["responsible", "Responsible staff", "text"], ["access_method", "Access method", "text"]],
  },
  complaints: {
    title: "Complaints", resource: "complaints",
    columns: [["customer", "Customer"], ["severity", "Severity"], ["status", "Status"]],
    fields: [["customer", "Customer", "text"], ["booking_ref", "Booking ref", "text"], ["description", "Description", "textarea"],
      ["severity", "Severity", "select", ["Minor", "Standard", "Urgent", "Safeguarding/Security"]], ["staff_involved", "Staff involved", "text"],
      ["resolution", "Resolution", "textarea"], ["compensation_request", "Compensation requested (£)", "number"],
      ["status", "Status", "select", ["Open", "Investigating", "Resolved", "Closed"]]],
  },
  checklist_templates: {
    title: "Cleaning Checklists", resource: "checklist_templates",
    columns: [["name", "Template"]],
    fields: [["name", "Template name", "text"]],
    note: "Task items are seeded and used automatically when a cleaner starts a job.",
  },
  commercial_enquiries: {
    title: "Commercial Leads", resource: "commercial_enquiries",
    columns: [["reference", "Ref"], ["company_name", "Company"], ["premises_type", "Premises"], ["num_sites", "Sites"], ["status", "Status"]],
    fields: [["company_name", "Company", "text"], ["contact_name", "Contact", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"],
      ["site_address", "Site address", "text"], ["premises_type", "Premises type", "text"], ["num_sites", "Number of sites", "number"],
      ["floor_area", "Approx. floor area", "text"], ["frequency", "Frequency", "text"], ["preferred_schedule", "Preferred schedule", "text"],
      ["estimated_hours", "Estimated hours", "text"], ["washrooms", "Washrooms", "text"], ["kitchens", "Kitchens", "text"],
      ["communal_areas", "Communal areas", "text"], ["specialist_requirements", "Specialist requirements", "textarea"],
      ["existing_provider", "Existing provider", "text"], ["start_date", "Desired start date", "date"],
      ["status", "Status", "select", ["Insurance Scope Review Required", "Approved for Quotation", "Quoted", "Won", "Lost"]],
      ["notes", "Notes", "textarea"]],
    note: "Every commercial enquiry starts as 'Insurance Scope Review Required'. Change to 'Approved for Quotation' once management confirms the activity is insured.",
  },
  commercial_sites: {
    title: "Commercial Sites", resource: "commercial_sites",
    columns: [["name", "Site"], ["client", "Client"], ["frequency", "Frequency"], ["assigned_staff", "Cleaner"], ["status", "Status"]],
    fields: [["client", "Client / company", "text"], ["name", "Site name", "text"], ["address", "Site address", "text"],
      ["site_contact", "Site contact", "text"], ["contact_phone", "Contact phone", "text"],
      ["schedule", "Cleaning schedule", "text"], ["frequency", "Frequency", "select", ["Daily", "Weekly", "Fortnightly", "Monthly", "Custom"]],
      ["checklist_template", "Site checklist template", "text"], ["assigned_staff", "Assigned cleaner", "text"],
      ["contract_value", "Contract value (£)", "number"], ["sla_notes", "SLA notes", "textarea"],
      ["status", "Status", "select", ["Active", "On Hold", "Ended"]]],
    note: "Each commercial client can hold multiple sites — each with its own schedule, checklist and assigned cleaner.",
  },
  messages: {
    title: "Messages (Contact Us)", resource: "messages",
    columns: [["name", "From"], ["email", "Email"], ["message", "Message"], ["handled", "Handled"]],
    fields: [["name", "Name", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"],
      ["company", "Company", "text"], ["message", "Message", "textarea"], ["handled", "Handled", "switch"]],
    note: "Contact-Us and customer-portal messages land here. Mark as handled once actioned.",
  },
  subscription_plans: {
    title: "Subscription Plans", resource: "subscription_plans",
    columns: [["name", "Plan"], ["price", "£/4wks"], ["cleans", "Cleans"], ["hours_each", "Hrs each"], ["tier", "Tier"], ["active", "Active"]],
    fields: [["name", "Plan name", "text"], ["price", "Price (£ / 4 weeks)", "number"], ["cycle_weeks", "Cycle (weeks)", "number"],
      ["cleans", "Cleans per cycle", "number"], ["hours_each", "Hours per clean", "number"],
      ["tier", "Tier", "select", ["Essential", "Premium", "Elite"]], ["description", "Description", "textarea"], ["active", "Active", "switch"], ["order", "Order", "number"]],
    note: "Memberships auto-renew every cycle until the customer cancels.",
  },
  customer_subscriptions: {
    title: "Customer Subscriptions", resource: "customer_subscriptions",
    columns: [["customer_name", "Customer"], ["plan_name", "Plan"], ["price", "£"], ["status", "Status"], ["next_renewal", "Renews"]],
    fields: [["customer_name", "Customer", "text"], ["plan_name", "Plan", "text"], ["price", "Price", "number"],
      ["status", "Status", "select", ["Active", "Paused", "Cancelled"]], ["next_renewal", "Next renewal", "date"], ["auto_renew", "Auto-renew", "switch"]],
  },
  credit_ledger: {
    title: "Cleaning Credits Ledger", resource: "credit_ledger",
    columns: [["user_email", "Customer"], ["type", "Type"], ["credits", "Credits"], ["value_gbp", "£ value"], ["note", "Note"]],
    fields: [["user_email", "Customer email", "text"], ["type", "Type", "select", ["purchase", "redemption", "promo", "adjustment"]],
      ["credits", "Credits (+/-)", "number"], ["value_gbp", "£ value", "number"], ["note", "Note", "text"]],
    note: "1 credit = £5 cleaning value. Add positive promo/loyalty credits here when authorised.",
  },
};

// ---------------- DataManager ----------------
export default function DataManager({ schemaKey }) {
  const s = SCHEMAS[schemaKey];
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get(`/res/${s.resource}`).then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [schemaKey]);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/res/${s.resource}/${editing.id}`, form);
      else await api.post(`/res/${s.resource}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/res/${s.resource}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fmt = (v) => {
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (Array.isArray(v)) return v.join(", ");
    return v ?? "—";
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="font-display text-3xl tracking-tight">{s.title}</h2>
          {s.note && <p className="text-xs text-[#8a8a8a] mt-1">{s.note}</p>}
        </div>
        <button data-testid={`add-${s.resource}`} onClick={() => setEditing({})} className="btn-gold px-4 py-2.5 rounded-sm text-sm flex items-center gap-2"><Plus size={16} /> Add</button>
      </div>
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">
            {s.columns.map(([k, l]) => <th key={k} className="px-4 py-3 font-medium">{l}</th>)}
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={s.columns.length + 1} className="px-4 py-10 text-center text-[#8a8a8a]">Loading…</td></tr> :
             rows.length === 0 ? <tr><td colSpan={s.columns.length + 1} className="px-4 py-10 text-center text-[#8a8a8a]">No records yet.</td></tr> :
             rows.map((r) => (
              <tr key={r.id} className="border-b border-[#f0efe9] hover:bg-[#faf9f5]" data-testid={`row-${s.resource}-${r.id}`}>
                {s.columns.map(([k], i) => <td key={k} className="px-4 py-3">{i === 0 ? <span className="font-medium">{fmt(r[k])} {r.demo && <DemoBadge />}</span> : fmt(r[k])}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} data-testid={`edit-${r.id}`} className="text-[#8a8a8a] hover:text-[#d4af37] mr-3"><Pencil size={16} /></button>
                  <button onClick={() => remove(r.id)} data-testid={`del-${r.id}`} className="text-[#8a8a8a] hover:text-red-500"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <RecordForm schema={s} record={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function RecordForm({ schema, record, onSave, onClose }) {
  const [form, setForm] = useState(() => {
    const init = { ...record };
    schema.fields.forEach(([k, , t]) => { if (init[k] === undefined) init[k] = t === "switch" ? false : t === "tags" ? [] : ""; });
    return init;
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const fieldCls = "w-full bg-white border border-[#e0ddd4] rounded-sm px-3 py-2.5 text-sm focus:border-[#d4af37]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl">{record.id ? "Edit" : "Add"} {schema.title}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="space-y-4">
          {schema.fields.map(([k, label, type, options]) => (
            <div key={k}>
              {type !== "switch" && <label className="text-xs tracking-wide text-[#555] uppercase mb-1 block">{label}</label>}
              {type === "textarea" ? <textarea rows="3" className={fieldCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`field-${k}`} /> :
               type === "select" ? <select className={fieldCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`field-${k}`}><option value="">Select…</option>{options.map((o) => <option key={o}>{o}</option>)}</select> :
               type === "switch" ? <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`field-${k}`} className="w-5 h-5 accent-[#d4af37]" /> {label}</label> :
               type === "tags" ? <input className={fieldCls} value={Array.isArray(form[k]) ? form[k].join(", ") : form[k] || ""} onChange={(e) => set(k, e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`field-${k}`} /> :
               type === "number" ? <input type="number" className={fieldCls} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))} data-testid={`field-${k}`} /> :
               type === "date" ? <input type="date" className={fieldCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`field-${k}`} /> :
               <input className={fieldCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`field-${k}`} />}
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={() => onSave(form)} data-testid="record-save" className="btn-gold px-6 py-2.5 rounded-sm text-sm">Save</button>
          <button onClick={onClose} className="btn-outline-gold px-6 py-2.5 rounded-sm text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}
